import { cloneElement, isValidElement, useId } from 'react';
import { s } from '../../theme/tokens';
import { Box } from './Box';
import { FormControl } from './FormControl';
import { FormHelperText } from './FormHelperText';
import { Text } from './Text';
import type { ReactElement, ReactNode } from 'react';

type FormFieldProps = {
  label?: ReactNode;
  helpText?: ReactNode;
  errorText?: ReactNode;
  required?: boolean;
  children:
    | ReactNode
    | ((props: {
        id: string;
        labelId?: string;
        label?: ReactNode;
        'aria-describedby'?: string;
      }) => ReactNode);
};

export const FormField = ({
  label,
  helpText,
  errorText,
  required,
  children,
}: FormFieldProps): ReactElement => {
  const fieldId = useId();
  const labelId = label ? `${fieldId}-label` : undefined;
  const helperId = `${fieldId}-helper`;
  const describedBy = errorText || helpText ? helperId : undefined;
  const hasError = Boolean(errorText);

  let field: ReactNode;
  if (typeof children === 'function') {
    field = children({ id: fieldId, labelId, label, 'aria-describedby': describedBy });
  } else if (isValidElement(children)) {
    const element = children as ReactElement<{
      id?: string;
      labelId?: string;
      label?: ReactNode;
      'aria-describedby'?: string;
    }>;
    field = cloneElement(element, {
      id: element.props.id ?? fieldId,
      labelId: element.props.labelId ?? labelId,
      label: element.props.label ?? label,
      'aria-describedby': element.props['aria-describedby'] ?? describedBy,
    });
  } else {
    field = children;
  }

  return (
    <FormControl error={hasError} required={required} fullWidth>
      <Box>
        {label ? (
          <Box sx={{ mb: s('sm') }}>
            <Text role="body" component="label" htmlFor={fieldId}>
              {label}
            </Text>
          </Box>
        ) : null}
        {field}
        {errorText ? (
          <FormHelperText id={helperId} error sx={{ mt: s('xs') }}>
            {errorText}
          </FormHelperText>
        ) : helpText ? (
          <FormHelperText id={helperId} sx={{ mt: s('xs') }}>
            {helpText}
          </FormHelperText>
        ) : null}
      </Box>
    </FormControl>
  );
};
